import React, { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Target,
  Lightbulb,
  BookOpen,
  Code2,
  Brain,
  Rocket,
  Trophy,
  Sparkles,
} from "lucide-react";
import BackIcon from "../../assets/course-details/left-arrow.png";
import User from "../../assets/course-details/user.svg";
import Star from "../../assets/course-details/star.svg";
import PriceTag from "../../assets/course-details/price-tag.svg";
import Button from "../../components/common/Button";
import Toast from "../../components/common/Toast";
import { BtnIcon, Heart } from "../../components/Icons";
import { useAddToCartMutation, useGetMyCartQuery } from "../../services/cart";
import Book from "../../assets/course-details/book.svg";
import LearnItem from "../../components/LearnItem";
import ModuleIcon from "../../assets/course-details/module-icon.svg";
import CTASection from "../../components/CTASection";
import StudentsReviews from "../../components/home/StudentsReviews";
import FAQ from "../../components/FAQ";
import StudentNavbar from "../../components/student/StudentNavbar";
import InstructorCard from "../../components/cards/InstructorCard";
import Avatar from "../../assets/course-details/Avatar.png";
import Thumbnail from "../../assets/course-details/what-you-learn-thumbnail.png";
import PricingCard from "../../components/cards/PricingCard";
import {
  useGetPublicCourseByIdQuery,
  useGetUserCourseFeedbackQuery,
} from "../../services/course";
import ReviewerProfile from "../../assets/reviewer-profile.png";
import ModuleCard from "../../components/cards/ModuleCard";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

const learningIcons = [
  Target,
  Lightbulb,
  BookOpen,
  Code2,
  Brain,
  Rocket,
  Trophy,
  Sparkles,
];

const SectionPlaceholder = ({
  type = "empty",
  title,
  description,
  onRetry,
  className = "",
}) => (
  <div
    className={`flex flex-col items-center justify-center gap-3 rounded-[18px] border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-6 py-10 text-center ${className}`}
  >
    {type === "loading" && (
      <>
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6636]" />
        <p className="text-base font-medium text-[#4B5563]">
          {title || "Loading..."}
        </p>
      </>
    )}
    {type === "error" && (
      <>
        <WifiOff className="h-8 w-8 text-[#FF6636]" />
        <p className="text-base font-semibold text-[#1F2937]">
          {title || "Connection problem"}
        </p>
        <p className="max-w-sm text-sm leading-5 text-[#6B7280]">
          {description ||
            "Please check your internet connection and try again."}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#FF6636] hover:underline"
          >
            <RefreshCw size={14} />
            Try again
          </button>
        )}
      </>
    )}
    {type === "empty" && (
      <>
        <p className="text-base font-semibold text-[#4B5563]">
          {title || "No data available"}
        </p>
        <p className="max-w-sm text-sm leading-5 text-[#9CA3AF]">
          {description || "Content for this section is not available yet."}
        </p>
      </>
    )}
  </div>
);
const CourseOverview = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const {
    data: publicCourseResponse,
    isLoading,
    isError,
    refetch,
  } = useGetPublicCourseByIdQuery(courseId, {
    skip: !courseId,
  });

  const { data: userCourseFeedbackResponse } = useGetUserCourseFeedbackQuery(
    courseId,
    {
      skip: !courseId,
    },
  );

  const courseReviews = useMemo(() => {
    const feedback = userCourseFeedbackResponse?.data;
    if (!feedback?._id) return [];

    let loggedInUser = null;
    try {
      loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "null");
    } catch {
      loggedInUser = null;
    }

    const user =
      typeof feedback.user === "object" && feedback.user !== null
        ? feedback.user
        : {
            firstName: loggedInUser?.firstName || "You",
            lastName: loggedInUser?.lastName || "",
            profilePicture: {
              secureUrl:
                loggedInUser?.profilePicture?.secureUrl ||
                loggedInUser?.profilePicture ||
                ReviewerProfile,
            },
          };

    return [{ ...feedback, user }];
  }, [userCourseFeedbackResponse]);

  const publicCourse = publicCourseResponse?.data;
  const course = publicCourse?.course;
  const courseOverview = course?.courseOverview || {};

  const { data: cartResponse } = useGetMyCartQuery();
  const cartItems = useMemo(() => {
    const d = cartResponse?.data;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d)) return d;
    return [];
  }, [cartResponse]);

  const isCourseInCart = useMemo(() => {
    if (!courseId) return false;
    return cartItems.some((item) => {
      const id =
        item?.courseId ??
        item?.course?._id ??
        (typeof item?.course === "string" ? item.course : null);
      return id != null && String(id) === String(courseId);
    });
  }, [cartItems, courseId]);

  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
  });

  const pageDescription = courseOverview?.courseDescription;
  const heroImageSrc = course?.courseThumbnail?.secureUrl;

  const handleBookDemo = () => {
    navigate(`/student/request-course-demo/${courseId}`);
  };

  const buyNowPrice = useMemo(() => {
    const raw = course?.coursePrice;
    if (raw == null || raw === "") return "99.9";
    const digits = String(raw).replace(/[^0-9.]/g, "");
    return digits || "99.9";
  }, [course?.coursePrice]);

  const coursePurchaseType =
    course?.courseEnrollementType === "live"
      ? "Live Classes"
      : "Recorded Classes";

  const handleCTABuyNow = () => {
    if (!courseId) return;
    navigate("/student/buy-course", {
      state: {
        price: buyNowPrice,
        courseId,
        purchaseType: coursePurchaseType,
      },
    });
  };

  // Add to Cart logic
  const [addToCart, { isLoading: isAddingToCart }] = useAddToCartMutation();
  const handleAddToCart = async () => {
    if (!courseId) return;
    if (isCourseInCart) {
      setToast({
        show: true,
        title: "Already in your wishlist",
        message: "This course is already in your wishlist.",
      });
      return;
    }
    try {
      await addToCart({
        courseId,
        purchaseType: "Recorded Lectures",
        quantity: 1,
      }).unwrap();
      setToast({
        show: true,
        title: "Added to wishlist",
        message: "This course has been added to your wishlist.",
      });
    } catch (err) {
      setToast({
        show: true,
        title: "Could not add to wishlist",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const apiModules = publicCourse?.curriculum;
  const modules = Array.isArray(apiModules)
    ? apiModules.map((item, idx) => ({
        id: idx + 1,
        title: item.moduleName,
        description: item.moduleDescription,
        icon: ModuleIcon,
      }))
    : [];

  const apiLearnSource = publicCourse?.learningOutcomes;

  const learnData = Array.isArray(apiLearnSource)
    ? apiLearnSource.map((item, index) => ({
        id: item._id || index,
        icon: learningIcons[index % learningIcons.length],
        description: item.outcomeDescription,
      }))
    : [];

  const instructors = [
    {
      id: 1,
      name: "Alex Johnson",
      email: "dmiller22@gmail.com",
      rating: "4.9",
      title: "Senior Full-Stack Developer at Tech Corp",
      description:
        "Seasoned full-stack developer with expertise in React, Node.js, and MongoDB. Former engineer at Google and Microsoft.",
      image: Avatar,
    },
    {
      id: 2,
      name: "Alex Johnson",
      email: "dmiller22@gmail.com",
      rating: "4.9",
      title: "Senior Full-Stack Developer at Tech Corp",
      description:
        "Seasoned full-stack developer with expertise in React, Node.js, and MongoDB. Former engineer at Google and Microsoft.",
      image: Avatar,
    },
    {
      id: 3,
      name: "Alex Johnson",
      email: "dmiller22@gmail.com",
      rating: "4.9",
      title: "Senior Full-Stack Developer at Tech Corp",
      description:
        "Seasoned full-stack developer with expertise in React, Node.js, and MongoDB. Former engineer at Google and Microsoft.",
      image: Avatar,
    },
    {
      id: 4,
      name: "Alex Johnson",
      email: "dmiller22@gmail.com",
      rating: "4.9",
      title: "Senior Full-Stack Developer at Tech Corp",
      description:
        "Seasoned full-stack developer with expertise in React, Node.js, and MongoDB. Former engineer at Google and Microsoft.",
      image: Avatar,
    },
  ];

  return (
    <section className="bg-[#F5F5F5]  pb-12 sm:pb-16 lg:pb-24">
      <StudentNavbar />
      <div className="px-5 sm:px-8">
        <div className="max-w-[1754px]  mt-1 2xl:mt-2.5  mx-auto  bg-white shadow-[0_0_4px_2px_#00000040] pt-8 sm:pt-12 lg:pt-16 xl:pt-[77px] pb-16  ">
          <div className="px-5 sm:px-8 lg:px-14 2xl:px-[72px]">
            <div className="flex flex-col  lg:flex-row justify-between gap-10">
              <div className="max-w-[740px] w-full">
                <Link to="/student" className="inline-block ml-0.5">
                  <img src={BackIcon} alt="Back" width={21} height={21} />
                </Link>
                <h1 className="mt-4 font-bold leading-9  sm:leading-[44px] tracking-[-1%] sm:mt-6 text-3xl sm:text-4xl 2xl:text-[40px] 2xl:leading-[56px] ">
                  Level Up {course?.courseTitle || "Your"} Skills With{" "}
                  <span className="text-[#FF6636]">
                    the Top Courses for Teens
                  </span>
                </h1>
                <p className="mt-4 max-w-[763px] leading-[19px]">
                  {pageDescription}
                </p>
                <div className="  mt-6 sm:mt-11">
                  <div  className="flex gap-1 items-center">
                    <img
                      src={PriceTag}
                      alt="icon"
                      style={{ width: 20, height: 20 }}
                    />
                    <p className="font-medium sm:text-lg leading-[21px] ">
                      {course?.coursePrice || "$99.9"}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex w-full flex-col gap-3 sm:mt-8 sm:gap-2.5 xl:flex-row xl:items-stretch [&>div]:w-full xl:[&>div]:w-auto">
                  <Button
                    icon={BtnIcon}
                    btnText="Book a Free Demo"
                    onClick={handleBookDemo}
                    btnClassName={
                      "bg-[#FF6636] hover:bg-opacity-15 hover:text-[#FF6636]   flex-row-reverse gap-3 font-semibold text-lg text-white px-9 py-[14px]"
                    }
                  />
                  <Button
                    icon={Heart}
                    btnText="Add to Wishlist"
                    btnClassName={
                      "border border-black gap-6 font-bold text-sm text-black px-9 py-[17px]" +
                      (isAddingToCart ? " opacity-60 cursor-not-allowed" : "")
                    }
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                  />
                </div>
              </div>
              <div className="hidden lg:block relative mx-auto mt-0 w-full max-w-[590px]  xl:max-w-[659px]  lg:mx-0 lg:mt-10">
                <div className="relative aspect-[590/400] xl:aspect-[659/480] w-full">
                  {isLoading ? (
                    <SectionPlaceholder
                      type="loading"
                      title="Loading course image..."
                      className="h-full w-full"
                    />
                  ) : isError ? (
                    <SectionPlaceholder
                      type="error"
                      title="Couldn't load course image"
                      description="We couldn't reach the server. Check your connection and try again."
                      onRetry={refetch}
                      className="h-full w-full"
                    />
                  ) : heroImageSrc ? (
                    <>
                      <img
                        src={heroImageSrc}
                        alt="hero"
                        className="relative z-10 h-full w-full object-cover"
                      />
                      <div className="pointer-events-none absolute   h-full w-full border-2 border-[#923D8C] shadow-[0_0_4px_0px_#00000040] -top-4 left-3 xl:-top-[30px] xl:left-[26px]" />
                    </>
                  ) : (
                    <SectionPlaceholder
                      type="empty"
                      title="No course image available"
                      description="The image for this course hasn't been added yet."
                      className="h-full w-full"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="mt-14 sm:mt-10 2xl:-mt-1 flex flex-col gap-3 sm:gap-4  sm:flex-row sm:items-center">
              <div className="shrink-0">
                <img src={Book} alt="book icon" />
              </div>
              <div>
                <h3 className="leading-tight tracking-normal text-3xl lg:text-[32px] lg:leading-[56px]">
                  What you’ll Learn
                </h3>
                <p className="text-base leading-5 sm:text-lg 2xl:max-w-max max-w-[588px] ">
                  Gain the skills that top companies are looking for in modern
                  web developers
                </p>
              </div>
            </div>

            <div className=" flex flex-col   xl:flex-row  justify-between gap-10  pt-5  2xl:pt-0">
              <div className="w-full space-y-5  2xl:mt-9">
                {isLoading ? (
                  <SectionPlaceholder
                    type="loading"
                    title="Loading learning outcomes..."
                  />
                ) : isError ? (
                  <SectionPlaceholder
                    type="error"
                    title="Couldn't load learning outcomes"
                    description="We couldn't reach the server. Check your connection and try again."
                    onRetry={refetch}
                  />
                ) : learnData.length > 0 ? (
                  learnData.map((itm, idx) => (
                    <LearnItem
                      key={itm.id}
                      icon={itm.icon}
                      description={itm.description}
                      index={idx}
                    />
                  ))
                ) : (
                  <SectionPlaceholder
                    type="empty"
                    title="No learning outcomes yet"
                    description="What you'll learn for this course will be shared here soon."
                  />
                )}
              </div>
              <div className="mt-[61px] relative mx-auto w-full max-w-[567px] shrink-0 xl:mx-0">
                <div className="relative aspect-[550/430]  w-full">
                  <img
                    src={Thumbnail}
                    alt="thumbnail"
                    className="absolute right-0 top-2.5 z-10 h-[calc(100%-10px)] w-[calc(100%-28px)] object-cover sm:right-12"
                  />
                  <div className="pointer-events-none h-[387px] w-[542px] border-2 border-[#FF6636] shadow-[0_0_4px_0px_#00000040]" />
                </div>
              </div>
            </div>

            <div className="mt-12 flex w-full flex-col gap-8 sm:mt-16 lg:mt-20 xl:mt-[83px] xl:flex-row xl:items-start xl:justify-between xl:gap-10">
              <div className="w-full min-w-0 xl:max-w-[780px] 2xl:max-w-[1037px]">
                <h3 className="text-2xl leading-tight tracking-normal sm:text-4xl lg:text-[40px] lg:leading-[56px]">
                  Our {course?.courseTitle || ""} Class Curriculum
                </h3>
                <p className="mt-3 text-sm leading-[19px] sm:mt-4 sm:text-base">
                  {isLoading
                    ? "Loading modules..."
                    : isError
                      ? "Unable to load modules"
                      : `${modules.length} Modules`}
                </p>

                <div className="mt-6 flex w-full flex-col gap-5 sm:mt-10 sm:gap-7 lg:mt-[60px]">
                  {isLoading ? (
                    <SectionPlaceholder
                      type="loading"
                      title="Loading curriculum..."
                    />
                  ) : isError ? (
                    <SectionPlaceholder
                      type="error"
                      title="Couldn't load curriculum"
                      description="We couldn't reach the server. Check your connection and try again."
                      onRetry={refetch}
                    />
                  ) : modules.length > 0 ? (
                    modules.map((module) => (
                      <ModuleCard key={module.id * 4} module={module} />
                    ))
                  ) : (
                    <SectionPlaceholder
                      type="empty"
                      title="No curriculum available yet"
                      description="Class modules and lessons for this course will appear here once added."
                    />
                  )}
                </div>
              </div>
              <div className="mx-auto w-full max-w-[567px] shrink-0 xl:mx-0 xl:ml-auto">
                <PricingCard
                  buyTo={courseId ? "/student/buy-course" : undefined}
                  buyState={
                    courseId
                      ? {
                          price: buyNowPrice,
                          courseId,
                          purchaseType: coursePurchaseType,
                        }
                      : undefined
                  }
                  demoTo={
                    courseId
                      ? `/student/request-course-demo/${courseId}`
                      : undefined
                  }
                  livePrice={course?.coursePrice ?? "$99.9"}
                  recordedPrice={course?.coursePrice ?? "$99.9"}
                />
              </div>
            </div>

            <div className="text-black mt-20">
              <h1 className="text-[40px] leading-[56px] tracking-normal">
                Meet Your Instructors
              </h1>
              <p className="mt-4 text-base">
                Learn from industry experts with years of real-world experience
              </p>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 2xl:gap-9">
              {instructors.map((instructor) => (
                <InstructorCard key={instructor.id} instructor={instructor} />
              ))}
            </div>
            <div className="mb-12 mt-12 lg:mb-[76px] lg:mt-[107px]">
              <CTASection
                title={`Start Mastering ${course?.courseTitle || "Web Development"} Today`}
                textSize="text-3xl sm:text-4xl lg:text-5xl xl:text-[55px]"
                subText={`Unlock your potential and dive into the world of ${course?.courseTitle || "web development"}
            with hands-on courses designed for all skill levels.`}
                btnText="Buy Now"
                btnSize="xl:px-[97px]"
                isAddToCartVisible={true}
                onBuyNowClick={handleCTABuyNow}
                onSecondaryClick={handleAddToCart}
                secondaryBtnText="Add to Wishlist"
                secondaryIcon={Heart}
                secondaryDisabled={isAddingToCart}
              />
            </div>
          </div>
          <StudentsReviews
            reviews={courseReviews}
            padding="pl-6 2xl:pl-[35px]"
            width="max-w-[1558px]"
          />
          <FAQ />
        </div>
      </div>

      {toast.show ? (
        <Toast
          title={toast.title}
          message={toast.message}
          duration={5000}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      ) : null}
    </section>
  );
};

export default CourseOverview;
